// Import statements with TypeScript extensions
import { db } from '../../config/main_config';
import { waitForCompanyID } from './waitForCompanyID.util';
import { createToast } from './createToast.util.js';
import { closeModal } from './closeModal.util';
import { Issue } from '../../../../interfaces/issue.interface';
import { IssueRegister } from '../../../../interfaces/issueRegister.interface';

// Helper function to get the current date and time in the required format
function getFormattedTimestamp(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
    };
    const timeString = now.toLocaleString('en-US', options).replace(/\s/g, ''); // 'HH:MM:SS_AM/PM'

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is zero-based
    const year = now.getFullYear();

    return `${day}_${month}_${year}@${timeString}`;
}

function getNextIssueID(existingIssues: IssueRegister | undefined): string {
    if (!existingIssues || Object.keys(existingIssues).length === 0) {
        return "ISU-1"; // Default issue ID if there are no existing issues
    }

    const issueIDs: number[] = Object.keys(existingIssues).map(issueID => {
        const parts = issueID.split('-');
        if (parts.length !== 2) return 0;
        const idNumber = parseInt(parts[1], 10);
        return isNaN(idNumber) ? 0 : idNumber; // Handle non-numeric IDs gracefully
    });

    const maxID = Math.max(...issueIDs);
    return `ISU-${maxID + 1}`;
}

// Function to save the current register to the history subcollection before adding the new issue
async function updateHistoryCollection(companyID: string, currentRegister: IssueRegister): Promise<void> {
    const timestamp = getFormattedTimestamp();
    const historyRef = db.collection(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register/history`).doc(timestamp);

    try {
        await historyRef.set(currentRegister);
        console.log(`Register snapshot saved to history as ${timestamp}`);
    } catch (error) {
        console.error('Error saving register snapshot to history:', error);
    }
}

async function saveNewIssue(companyID: string): Promise<void> {
    // Helper function to safely get input values
    const getInputValue = (id: string): string => {
        const element = document.getElementById(id) as HTMLInputElement | null;
        return element?.value.trim() || '';
    };

    const dateAdded = getInputValue('dateAdded_new');
    const issueType = getInputValue('issueType_new');
    const type = getInputValue('type_new');
    const name = getInputValue('name_new');
    const description = getInputValue('description_new');
    const risks = window.selectedRisks || [];
    const objectives = window.selectedISMSObjectives || [];
    const legalObligations = window.selectedLegalObligations || [];
    const frameworks = window.selectedControls || [];

    const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register`);

    try {
        // Fetch the current state of the register
        const docSnapshot = await documentRef.get();
        let currentRegister: IssueRegister = {};
        let newIssueID = "ISU-1"; // Default issue ID

        if (docSnapshot.exists) {
            const data = docSnapshot.data();
            currentRegister = data as IssueRegister;

            // Get the next issue ID based on existing issues
            newIssueID = getNextIssueID(currentRegister);

            // Save the current register to the history subcollection before adding the new issue
            await updateHistoryCollection(companyID, currentRegister);
        }

        const newIssue: Issue = {
            id: newIssueID,
            name,
            description,
            type,
            dateAdded,
            details: {
                issueType,
                risks,
                objectives,
                legalObligations,
                frameworks
            }
        };

        // Add the new issue to the register
        await documentRef.set(
            {
                [newIssueID]: newIssue
            },
            { merge: true }
        );

        console.log('New issue added successfully:', newIssue);

    } catch (error) {
        console.error('Error adding new issue:', error);
    }
}

// Triggered when the "Save" button is clicked
export async function addNewIssue(event: Event): Promise<void> {
    event.preventDefault();

    const companyID = await waitForCompanyID(); // Assume this function fetches the current company ID

    if (companyID) {
        await saveNewIssue(companyID);
        closeModal(null, true);
        createToast({
            message: 'Issue Added successfully.'
        });
    } else {
        alert('Error: Company ID not found.');
    }
}